import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import { useNavigate, useParams } from "react-router-dom";
import { pushApi } from "../../api/push";
import {
  getPanelPushSubscription,
  subscribePanelPush,
} from "../../utils/webPushClient";
import {
  OWNER_PUSH_DEFAULT_PREFERENCES,
  currentNotificationPermission,
  normalizeOwnerPushPreferences,
  resolveProjectPushState,
} from "../../utils/projectPushState";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const PREFERENCE_OPTIONS = [
  ["storefrontActivity", "Visitas y navegación", "Incluye cada entrada al home, catálogo, producto, contacto, carrito y checkout."],
  ["commercial", "Acciones comerciales", "WhatsApp, teléfono, email, carrito y otras señales de intención."],
  ["operations", "Productos y página", "Altas, ediciones, bajas, imágenes, landing, agenda e integraciones."],
  ["erp", "ERP, caja y stock", "Movimientos operativos realizados por dueños o empleados."],
  ["orders", "Pedidos", "Nuevos pedidos y cambios relevantes de estado."],
  ["payments", "Pagos", "Pagos aprobados, rechazados o pendientes."],
  ["billing", "Suscripción de WebProfit", "Vencimientos, renovaciones, pausas y reactivaciones."],
  ["security", "Accesos y usuarios", "Cambios de usuarios, permisos y eventos de seguridad."],
  ["panelActivity", "Navegación del equipo", "Recorridos de dueños y empleados por el panel. Puede generar muchas alertas."],
  ["system", "Sistema", "Pruebas, fallos técnicos y movimientos generales."],
];

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function Metric({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(25,36,47,.08)" }}>
      <CardContent sx={{ p: { xs: 1.05, sm: 2 }, "&:last-child": { pb: { xs: 1.05, sm: 2 } } }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: .5, sm: 1.15 }} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Box sx={{ width: { xs: 30, sm: 40 }, height: { xs: 30, sm: 40 }, borderRadius: 2.2, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange, "& svg": { fontSize: { xs: 17, sm: 22 } } }}>{icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.58)", fontWeight: 900, lineHeight: 1.15 }}>{title}</Typography>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: 21, sm: 28 }, lineHeight: 1.1, mt: .1 }}>{value}</Typography>
            <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.50)" }}>{helper}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProjectNotificationsPage({ embedded = false }) {
  const isMobileViewport = useMediaQuery("(max-width:899.95px)", { noSsr: true });
  const compactMobile = embedded && isMobileViewport;
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [pushState, setPushState] = React.useState(null);
  const [preferences, setPreferences] = React.useState(OWNER_PUSH_DEFAULT_PREFERENCES);
  const [loading, setLoading] = React.useState(true);
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [form, setForm] = React.useState({ audience: "storefront", title: "", body: "", url: "/#/productos" });

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await pushApi.status(projectId);
      const resolved = await resolveProjectPushState(response);
      setData(response);
      setPushState(resolved);
      setPreferences(normalizeOwnerPushPreferences(resolved.projectSubscription?.preferences || OWNER_PUSH_DEFAULT_PREFERENCES));
    } catch (err) {
      setError(err.message || "No se pudo cargar Web Push");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const saveSubscription = async (successMessage) => {
    const subscription = await subscribePanelPush(data?.publicKey);
    await pushApi.subscribe(projectId, subscription.toJSON(), preferences);
    setMessage(successMessage);
    await load();
  };

  const activate = async () => {
    try {
      setWorking(true); setError(""); setMessage("");
      await saveSubscription("Este navegador ya recibe actividad y movimientos del proyecto.");
    } catch (err) {
      const permission = currentNotificationPermission();
      if (permission === "denied") setError("Este navegador tiene las notificaciones bloqueadas.");
      else if (permission === "default") setError("No se activaron las notificaciones. Podés intentarlo nuevamente cuando quieras.");
      else setError(err.message || "No se pudieron activar las notificaciones");
      await load();
    } finally { setWorking(false); }
  };

  const savePreferences = async () => {
    try {
      setWorking(true); setError(""); setMessage("");
      await saveSubscription("Preferencias de alertas guardadas para este navegador.");
    } catch (err) {
      setError(err.message || "No se pudieron guardar las preferencias");
    } finally { setWorking(false); }
  };

  const deactivate = async () => {
    try {
      setWorking(true); setError(""); setMessage("");
      const subscription = await getPanelPushSubscription();
      if (subscription?.endpoint) await pushApi.unsubscribe(projectId, subscription.endpoint);
      setMessage("Se desactivaron solamente las alertas de este proyecto. Las demás suscripciones del navegador siguen intactas.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudieron desactivar las notificaciones");
    } finally { setWorking(false); }
  };

  const ownerSubscribers = Number(data?.ownerSubscribers) || 0;
  const storefrontSubscribers = Number(data?.storefrontSubscribers) || 0;

  const audienceUnavailableMessage = React.useMemo(() => {
    if (form.audience === "storefront" && storefrontSubscribers === 0) {
      return "Todavía no hay visitantes suscriptos para enviarles esta notificación.";
    }
    if (form.audience === "owner" && ownerSubscribers === 0) {
      return "Todavía no hay suscripciones activas del equipo.";
    }
    if (form.audience === "all" && storefrontSubscribers === 0 && ownerSubscribers === 0) {
      return "Todavía no hay suscripciones activas en ninguna audiencia.";
    }
    return "";
  }, [form.audience, ownerSubscribers, storefrontSubscribers]);

  const audienceLabel = form.audience === "owner"
    ? "Dueños y empleados"
    : form.audience === "all"
      ? "Visitantes + equipo"
      : "Visitantes suscriptos";

  const audienceReach = form.audience === "owner"
    ? `${ownerSubscribers} ${ownerSubscribers === 1 ? "suscripción activa del equipo" : "suscripciones activas del equipo"}`
    : form.audience === "all"
      ? `${storefrontSubscribers} de visitantes + ${ownerSubscribers} del equipo`
      : `${storefrontSubscribers} ${storefrontSubscribers === 1 ? "suscripción activa de visitantes" : "suscripciones activas de visitantes"}`;

  const prepareSend = (event) => {
    event.preventDefault();
    if (working) return;
    setError("");
    setMessage("");
    if (audienceUnavailableMessage) return;
    setConfirmOpen(true);
  };

  const send = async () => {
    if (working || audienceUnavailableMessage) return;
    try {
      setWorking(true); setError(""); setMessage("");
      const response = await pushApi.send(projectId, form);
      const result = response.result || {};
      setMessage(`Notificación procesada: ${result.sent || 0} enviadas, ${result.failed || 0} fallidas.`);
      setForm((current) => ({ ...current, title: "", body: "" }));
      setConfirmOpen(false);
      await load();
    } catch (err) {
      setConfirmOpen(false);
      setError(err.message || "No se pudo enviar la notificación");
    } finally { setWorking(false); }
  };

  const canSend = data?.canSend === true;
  const browserSubscribed = pushState?.activeForCurrentProject === true;
  const browserBlocked = pushState?.blocked === true;
  const browserSupported = pushState?.supported !== false;

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={{ xs: 1, md: 2 }}>
        <Box sx={{ display: compactMobile ? "none" : "block" }}>
          <Stack direction="row" spacing={.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant={embedded ? "h5" : "h3"} sx={{ fontWeight: 950, color: BRAND.dark }}>Notificaciones Web Push</Typography>
            <ContextualHelp
              compact
              title="Cómo se separan las alertas"
              text="El equipo recibe alertas operativas en sus navegadores. Los visitantes forman otra audiencia y sólo reciben promociones si aceptaron suscribirse."
            />
          </Stack>
          <Typography sx={{ mt: .35, color: "rgba(25,36,47,.64)", maxWidth: 900, fontSize: { xs: ".88rem", sm: ".95rem" } }}>
            Alertas internas y promociones, separadas por audiencia.
          </Typography>
        </Box>
        <Chip
          icon={browserSubscribed ? <NotificationsActiveRoundedIcon /> : <NotificationsOffRoundedIcon />}
          label={browserSubscribed ? "Alertas internas activas" : browserBlocked ? "Bloqueadas en este navegador" : "Alertas internas desactivadas"}
          color={browserSubscribed ? "success" : browserBlocked ? "warning" : "default"}
          sx={{ fontWeight: 900, alignSelf: "flex-start" }}
        />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      {!data?.enabled && !loading && <Alert severity="warning" action={<Button color="inherit" startIcon={<WorkspacePremiumRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectId}/configuracion/suscripcion?feature=Notificaciones%20Web%20Push`)} sx={{ textTransform: "none", fontWeight: 900 }}>Mejorar plan</Button>}>El módulo Web Push no está activo en este proyecto.</Alert>}
      {data?.enabled && !data?.configured && <Alert severity="warning">El módulo está activo, pero todavía faltan las claves VAPID en el backend.</Alert>}
      {browserBlocked ? (
        <Alert severity="warning">
          <Typography component="span" sx={{ fontWeight: 900 }}>Este navegador tiene las notificaciones bloqueadas.</Typography>
          <Box component="details" sx={{ mt: .55, "& summary": { cursor: "pointer", fontWeight: 850 } }}>
            <Typography component="summary">Cómo habilitarlas</Typography>
            <Typography variant="body2" sx={{ mt: .55 }}>Abrí la configuración de permisos de este sitio en tu navegador, permití las notificaciones y luego volvé a esta pantalla.</Typography>
          </Box>
        </Alert>
      ) : null}
      {pushState && !browserSupported ? <Alert severity="info">Este navegador no admite notificaciones web. Podés seguir usando WebProfit normalmente.</Alert> : null}

      <Grid container spacing={{ xs: 1, sm: 2 }}>
        <Grid item xs={4}><Metric icon={<NotificationsActiveRoundedIcon />} title="Equipo" value={ownerSubscribers} helper="Navegadores internos" /></Grid>
        <Grid item xs={4}><Metric icon={<PeopleAltRoundedIcon />} title="Visitantes" value={storefrontSubscribers} helper="Solo promociones opt-in" /></Grid>
        <Grid item xs={4}><Metric icon={<CampaignRoundedIcon />} title="Envíos" value={data?.recentCampaigns?.length || 0} helper="Historial manual" /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: { xs: 1.35, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { xs: 18, sm: 24 } }}>Qué movimientos querés recibir</Typography>
          <ContextualHelp compact title="Qué cambia este control" text="El historial se guarda siempre. Estas preferencias sólo deciden qué eventos disparan una alerta en este navegador." />
        </Stack>
        <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mt: .5 }}>
          {PREFERENCE_OPTIONS.map(([key, label, helper]) => (
            <Grid item xs={12} sm={6} key={key}>
              <Box sx={{ p: { xs: 1.05, sm: 1.35 }, borderRadius: 3, bgcolor: "#F6F7FB" }}>
                <FormControlLabel
                  control={<Switch checked={preferences[key] !== false} onChange={(event) => setPreferences((current) => ({ ...current, [key]: event.target.checked }))} />}
                  label={<Typography sx={{ fontWeight: 900, color: BRAND.dark, lineHeight: 1.25 }}>{label}</Typography>}
                  sx={{ width: "100%", minHeight: 44, alignItems: "center", m: 0, justifyContent: "space-between", flexDirection: "row-reverse", gap: 1 }}
                />
                {isMobileViewport ? (
                  <Box component="details" sx={{ mt: .15, color: "rgba(25,36,47,.56)", "& summary": { cursor: "pointer", fontSize: 12, fontWeight: 850 } }}>
                    <Typography component="summary">Qué incluye</Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: .55, lineHeight: 1.4 }}>{helper}</Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.56)", mt: .25, lineHeight: 1.4 }}>{helper}</Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5, "& .MuiButton-root": { minHeight: 44, width: { xs: "100%", sm: "auto" } } }}>
          {!browserSubscribed ? (
            browserBlocked || !browserSupported ? null : (
              <Button disabled={working || !data?.enabled || !data?.configured} onClick={activate} startIcon={<NotificationsActiveRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Activar alertas internas</Button>
            )
          ) : (
            <>
              <Button disabled={working} onClick={savePreferences} startIcon={<SaveRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Guardar preferencias</Button>
              <Button disabled={working} onClick={deactivate} startIcon={<NotificationsOffRoundedIcon />} color="warning" variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Desactivar para este proyecto</Button>
            </>
          )}
        </Stack>
      </Paper>

      {canSend && (
        <Paper component="form" onSubmit={prepareSend} elevation={0} sx={{ p: { xs: 1.35, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
          <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: { xs: 1.25, sm: 2 } }}>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { xs: 18, sm: 24 } }}>Enviar una notificación manual</Typography>
            <ContextualHelp compact title="Elegir audiencia" text="Visitantes envía una promoción; Equipo envía una prueba o aviso interno. Ambas audiencias se procesan por separado." />
          </Stack>
          <Grid container spacing={{ xs: 1.25, sm: 2 }}>
            <Grid item xs={12} md={4}><TextField select fullWidth label="Audiencia" value={form.audience} onChange={(event) => setForm((current) => ({ ...current, audience: event.target.value }))}><MenuItem value="storefront">Visitantes suscriptos: promociones</MenuItem><MenuItem value="owner">Dueños y empleados: aviso interno</MenuItem><MenuItem value="all">Ambas audiencias, separadas</MenuItem></TextField></Grid>
            <Grid item xs={12} md={8}><TextField fullWidth label="Título" value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} inputProps={{ maxLength: 160 }} required /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Mensaje" value={form.body} onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))} inputProps={{ maxLength: 1000 }} required /></Grid>
            <Grid item xs={12} md={8}><TextField fullWidth label="URL al abrir" value={form.url} onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))} helperText="Ejemplo: /#/productos o /#/dashboard/proyectos/.../actividad" /></Grid>
            <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center" }}><Button type="submit" fullWidth disabled={working || !data?.enabled || !data?.configured || Boolean(audienceUnavailableMessage)} startIcon={<SendRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Enviar ahora</Button></Grid>
            {audienceUnavailableMessage ? <Grid item xs={12}><Alert severity="info">{audienceUnavailableMessage}</Alert></Grid> : null}
          </Grid>
        </Paper>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => { if (!working) setConfirmOpen(false); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: { xs: 3, sm: 4 }, mx: { xs: 1.5, sm: 4 }, maxHeight: "calc(100dvh - 24px)" } }}
      >
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark, pb: 1 }}>¿Enviar esta notificación?</DialogTitle>
        <DialogContent dividers sx={{ overflowX: "hidden", px: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 900 }}>Audiencia</Typography>
              <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>{audienceLabel}</Typography>
              <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.58)", fontWeight: 900, mt: 1.2 }}>Suscripciones activas</Typography>
              <Typography variant="body2" sx={{ color: "rgba(25,36,47,.68)", mt: .2 }}>{audienceReach}</Typography>
            </Box>
            <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: "#F6F7FB" }}>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 900 }}>Título</Typography>
              <Typography sx={{ color: BRAND.dark, fontWeight: 900, overflowWrap: "anywhere" }}>{form.title}</Typography>
              <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.58)", fontWeight: 900, mt: 1.4 }}>Mensaje</Typography>
              <Typography variant="body2" sx={{ color: "rgba(25,36,47,.78)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{form.body}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 900 }}>Destino al abrir</Typography>
              <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 800, overflowWrap: "anywhere" }}>{form.url || "—"}</Typography>
            </Box>
            <Alert severity="info" sx={{ "& .MuiAlert-message": { minWidth: 0 } }}>
              WebProfit respetará las preferencias de notificación de cada suscripción; la cantidad entregada puede ser menor.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1, flexDirection: { xs: "column-reverse", sm: "row" }, "& .MuiButton-root": { minHeight: 44, width: { xs: "100%", sm: "auto" } } }}>
          <Button disabled={working} onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none", fontWeight: 900 }}>Volver a editar</Button>
          <Button disabled={working} onClick={send} startIcon={<SendRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
            {working ? "Enviando…" : "Enviar notificación"}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={0} sx={{ p: { xs: 1.35, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { xs: 18, sm: 24 } }}>Envíos manuales recientes</Typography>
        <Stack spacing={1} sx={{ mt: 1.25 }}>
          {(data?.recentCampaigns || []).map((campaign) => (
            <Box key={campaign.id || campaign._id} sx={{ p: { xs: 1.15, sm: 1.6 }, borderRadius: 3, bgcolor: "#F6F7FB" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.85}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{campaign.title}</Typography>
                  {isMobileViewport ? (
                    <Box component="details" sx={{ mt: .45, color: "rgba(25,36,47,.62)", "& summary": { cursor: "pointer", fontSize: 12, fontWeight: 850 } }}>
                      <Typography component="summary">Ver mensaje</Typography>
                      <Typography variant="body2" sx={{ mt: .55, color: "rgba(25,36,47,.62)", lineHeight: 1.45 }}>{campaign.body}</Typography>
                    </Box>
                  ) : <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>{campaign.body}</Typography>}
                </Box>
                <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={campaign.audience} />
                  <Chip size="small" color="success" label={`${campaign.sent || 0} enviados`} />
                  {campaign.failed ? <Chip size="small" color="warning" label={`${campaign.failed} fallidos`} /> : null}
                </Stack>
              </Stack>
              <Typography variant="caption" sx={{ mt: .7, display: "block", color: "rgba(25,36,47,.5)" }}>{dateLabel(campaign.createdAt)}</Typography>
            </Box>
          ))}
          {!data?.recentCampaigns?.length && <Alert severity="info">Todavía no hay envíos manuales registrados.</Alert>}
        </Stack>
      </Paper>
    </Stack>
  );
}
